import { useEffect, useRef } from "react";
import { addDoc, collection, Timestamp } from "firebase/firestore";

const MIN_INTERVAL_MS = 30_000;
const MIN_DISTANCE_M = 25;
const RETENTION_DAYS = 45;

const distanceMeters = (a, b) => {
  if (!a || !b) return Infinity;
  const R = 6371000;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const lat1 = toRad(a.lat);
  const lat2 = toRad(b.lat);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

export default function useLiveTracking(db, entryId, enabled) {
  const lastWriteRef = useRef({ at: 0, point: null });
  const watchIdRef = useRef(null);

  useEffect(() => {
    if (!enabled || !entryId || !db) return;
    if (!("geolocation" in navigator)) {
      console.warn("[useLiveTracking] geolocation unavailable");
      return;
    }

    const write = async (lat, lng, accuracy) => {
      try {
        const expireAt = Timestamp.fromMillis(Date.now() + RETENTION_DAYS * 86400 * 1000);
        await addDoc(collection(db, "timeEntries", entryId, "track"), {
          lat,
          lng,
          accuracy: accuracy ?? null,
          ts: Timestamp.now(),
          expireAt,
        });
      } catch (err) {
        console.error("[useLiveTracking] write failed", err);
      }
    };

    const handlePosition = (pos) => {
      const { latitude: lat, longitude: lng, accuracy } = pos.coords;
      const now = Date.now();
      const last = lastWriteRef.current;
      const dist = distanceMeters(last.point, { lat, lng });
      const longEnough = now - last.at >= MIN_INTERVAL_MS;
      const movedEnough = dist >= MIN_DISTANCE_M;
      if (!last.point || (longEnough && movedEnough) || now - last.at >= MIN_INTERVAL_MS * 4) {
        lastWriteRef.current = { at: now, point: { lat, lng } };
        write(lat, lng, accuracy);
      }
    };

    const handleError = (err) => {
      console.warn("[useLiveTracking] geolocation error", err.code, err.message);
    };

    watchIdRef.current = navigator.geolocation.watchPosition(handlePosition, handleError, {
      enableHighAccuracy: true,
      maximumAge: 10_000,
      timeout: 20_000,
    });

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      lastWriteRef.current = { at: 0, point: null };
    };
  }, [db, entryId, enabled]);
}
